/**
 * Editors for database values, one per property type. Shared by the table, the
 * board cards and the property panel of a row page.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from 'radix-ui';
import { Check, ChevronDown, ExternalLink, FileText, Plus, RotateCw, X } from 'lucide-react';
import {
  asList,
  isRecurrenceRule,
  linkTarget,
  optionOf,
  splitDateTime,
  withOption,
  type PropertyDef,
} from '@nexus/core';
import { recurTaskPage, type LoadedDatabase } from '@nexus/workspace';
import { useStore } from '../../db/context';
import { cn } from '../../lib/cn';
import { formatDue } from '../tasks/format';
import { useSynced } from './useSynced';
import { searchCatalog, useCatalog } from '../../state/catalog';
import { useUi } from '../../state/ui';
import { DOT_CLASS, OPTION_CLASS } from './colors';

const field =
  'min-w-0 w-full rounded px-1.5 py-0.5 bg-transparent hover:bg-bg-hover focus:bg-bg-hover outline-none text-sm';

/** A coloured option chip (status values get a dot). */
export function OptionChip({ def, value }: { def: PropertyDef; value: string }) {
  const option = optionOf(def, value);
  const color = option?.color ?? 'gray';
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1 truncate rounded-md px-1.5 py-px text-xs',
        OPTION_CLASS[color],
      )}
    >
      {def.type === 'status' && (
        <span className={cn('size-1.5 shrink-0 rounded-full', DOT_CLASS[color])} />
      )}
      <span className="truncate">{value}</span>
    </span>
  );
}

function TextValue({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  list,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'number' | 'url' | 'email' | 'tel';
  placeholder?: string;
  list?: string;
}) {
  const [text, setText, focus] = useSynced(value);
  return (
    <input
      type={type}
      aria-label={label}
      value={text}
      placeholder={placeholder}
      list={list}
      onChange={(e) => setText(e.target.value)}
      onFocus={focus.onFocus}
      onBlur={() => {
        focus.onBlur();
        if (text !== value) onChange(text);
      }}
      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
      className={field}
    />
  );
}

/** A date with an optional time; stored as "2026-10-05" or "2026-10-05T14:00". */
export function DateTimeEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string | null) => void;
}) {
  const { t } = useTranslation();
  const parsed = splitDateTime(value);
  const [date, setDate, focus] = useSynced(parsed?.date ?? '');
  const [time, setTime] = useSynced(parsed?.time ?? '');
  const commit = (d: string, tm: string) => {
    const next = d ? (tm ? `${d}T${tm}` : d) : null;
    if (next !== (value || null)) onChange(next);
  };
  return (
    <span
      onFocusCapture={focus.onFocus}
      onBlurCapture={focus.onBlur}
      className="flex min-w-0 items-center gap-1"
    >
      <input
        type="date"
        aria-label={label}
        value={date}
        onChange={(e) => setDate(e.target.value)}
        onBlur={() => commit(date, date ? time : '')}
        className={cn(field, 'w-auto flex-none')}
      />
      {date && (
        <input
          type="time"
          aria-label={t('props.timeOf', { key: label })}
          value={time}
          onChange={(e) => setTime(e.target.value)}
          onBlur={() => commit(date, time)}
          className={cn(field, 'w-24 flex-none', !time && 'text-fg-muted')}
        />
      )}
    </span>
  );
}

function Picker({
  label,
  trigger,
  children,
}: {
  label: string;
  trigger: ReactNode;
  children: ReactNode;
}) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          className="group/pick flex min-h-6 w-full min-w-0 items-center gap-1 rounded px-1.5 py-0.5 text-left hover:bg-bg-hover"
        >
          <span className="flex min-w-0 flex-wrap items-center gap-1">{trigger}</span>
          <ChevronDown
            aria-hidden
            className="size-3.5 shrink-0 text-fg-muted opacity-60 group-hover/pick:opacity-100"
          />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          className="z-[70] flex max-h-80 w-64 flex-col overflow-auto rounded-xl border border-border bg-bg p-1 text-sm shadow-pop"
        >
          {children}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** Choose (or create) options; one for select/status, several for multi-select. */
function SelectValue({
  def,
  value,
  onChange,
  onDefChange,
}: {
  def: PropertyDef;
  value: unknown;
  onChange: (v: unknown) => void;
  onDefChange: (def: PropertyDef) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const multi = def.type === 'multi_select' || def.type === 'person';
  // Herhaling: only rules Nexus understands ("elke maandag", "elke 2 weken als klaar").
  const recurrence = def.keys?.includes('recurrence') ?? false;
  const invalid = recurrence && !!query.trim() && !isRecurrenceRule(query);
  const selected = multi ? asList(value) : value ? [String(value)] : [];
  const isSelected = (name: string) => selected.some((s) => s.toLowerCase() === name.toLowerCase());
  const options = (def.options ?? []).filter((o) =>
    o.name.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const exact = (def.options ?? []).some(
    (o) => o.name.toLowerCase() === query.trim().toLowerCase(),
  );
  const choose = (name: string) => {
    if (!multi) return onChange(isSelected(name) ? null : name);
    onChange(
      isSelected(name)
        ? selected.filter((s) => s.toLowerCase() !== name.toLowerCase())
        : [...selected, name],
    );
  };
  const create = () => {
    const name = query.trim();
    if (!name || (recurrence && !isRecurrenceRule(name))) return;
    onDefChange(withOption(def, name));
    choose(name);
    setQuery('');
  };
  return (
    <Picker
      label={def.name}
      trigger={
        selected.length ? (
          selected.map((s) => <OptionChip key={s} def={def} value={s} />)
        ) : (
          <span className="text-sm text-fg-muted">
            {t(def.type === 'person' ? 'db.choosePerson' : 'db.choose')}
          </span>
        )
      }
    >
      <input
        autoFocus
        value={query}
        aria-label={t('db.searchOrCreate')}
        placeholder={t(def.type === 'person' ? 'db.searchOrAddPerson' : 'db.searchOrCreate')}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (options[0] && (exact || !query.trim())) choose(options[0].name);
            else create();
          }
        }}
        className="mb-1 rounded-md border border-border bg-bg px-2 py-1 text-sm outline-none focus:border-accent"
      />
      <div role="listbox" aria-label={def.name} aria-multiselectable={multi}>
        {options.map((o) => (
          <button
            key={o.name}
            type="button"
            role="option"
            aria-selected={isSelected(o.name)}
            onClick={() => choose(o.name)}
            className="flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-bg-hover"
          >
            <span className="min-w-0 flex-1">
              <OptionChip def={def} value={o.name} />
            </span>
            {isSelected(o.name) && <Check className="size-3.5 text-fg-muted" />}
          </button>
        ))}
      </div>
      {recurrence && (
        <p className={cn('px-2 py-1 text-xs', invalid ? 'text-danger' : 'text-fg-muted')}>
          {t(invalid ? 'db.recurrenceInvalid' : 'db.recurrenceHint')}
        </p>
      )}
      {query.trim() && !exact && !invalid && (
        <button
          type="button"
          onClick={create}
          className="flex items-center gap-2 rounded px-2 py-1 text-left hover:bg-bg-hover"
        >
          <Plus className="size-3.5" />
          {t('db.createOption', { name: query.trim() })}
        </button>
      )}
      {selected.length > 0 && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="mt-1 flex items-center gap-2 rounded border-t border-border px-2 py-1 text-left text-fg-muted hover:bg-bg-hover"
        >
          <X className="size-3.5" />
          {t('db.clear')}
        </button>
      )}
    </Picker>
  );
}

/** A link to another page, stored as "[[Title]]". */
function PageValue({
  label,
  value,
  onChange,
}: {
  label: string;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const { t } = useTranslation();
  const open = useUi((s) => s.open);
  const pages = useCatalog((s) => s.pages);
  const title = asList(value).map(linkTarget)[0] ?? '';
  const target = title
    ? searchCatalog(title, 5).find((p) => p.title.toLowerCase() === title.toLowerCase())
    : undefined;
  const listId = `nx-pages-${label.replace(/\W/g, '')}`;
  return (
    <span className="flex min-w-0 flex-1 items-center gap-1">
      <TextValue
        label={label}
        value={title}
        placeholder={t('db.linkPage')}
        list={listId}
        onChange={(v) => onChange(v.trim() ? `[[${v.trim()}]]` : null)}
      />
      <datalist id={listId}>
        {pages.slice(0, 200).map((p) => (
          <option key={p.id} value={p.title} />
        ))}
      </datalist>
      {target && (
        <button
          type="button"
          aria-label={t('db.openPage', { title: target.title })}
          onClick={() => open({ kind: 'page', pageId: target.id })}
          className="rounded p-0.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          <ExternalLink className="size-3.5" />
        </button>
      )}
    </span>
  );
}

const RELATIVE_RE = /^-?(\d+)\s*(m|min|h|u|d|w)$/;
const RELATIVE_CHOICES = ['15m', '30m', '1h', '2h', '1d', '2d', '1w'];

/** A reminder: at a moment, or a fixed time before the due date ("1d" = one day before). */
export function ReminderEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string | null) => void;
}) {
  const { t } = useTranslation();
  const rel = RELATIVE_RE.exec(value.trim());
  const unit = rel ? (({ min: 'm', u: 'h' } as Record<string, string>)[rel[2]!] ?? rel[2]!) : '';
  const relative = rel ? `${rel[1]}${unit}` : null;
  const choices =
    relative && !RELATIVE_CHOICES.includes(relative)
      ? [relative, ...RELATIVE_CHOICES]
      : RELATIVE_CHOICES;
  const [mode, setMode] = useState(relative ?? 'at');
  const [seen, setSeen] = useState(relative);
  if (seen !== relative) {
    // The stored value changed elsewhere: follow it.
    setSeen(relative);
    setMode(relative ?? 'at');
  }
  return (
    <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
      <select
        aria-label={label}
        value={mode}
        onChange={(e) => {
          setMode(e.target.value);
          if (e.target.value !== 'at') onChange(e.target.value);
        }}
        className={cn(field, 'w-auto flex-none bg-bg')}
      >
        <option value="at">{t('props.reminderAt')}</option>
        {choices.map((c) => (
          <option key={c} value={c}>
            {t('props.reminderBefore', {
              amount: c.slice(0, -1),
              unit: t(`props.units.${c.slice(-1)}`, { count: Number(c.slice(0, -1)) }),
            })}
          </option>
        ))}
      </select>
      {mode === 'at' && (
        <DateTimeEditor
          label={t('props.reminderMoment')}
          value={relative ? '' : value}
          onChange={onChange}
        />
      )}
    </span>
  );
}

/** Open a page by its title (links in relations and backlinks are titles). */
function useOpenByTitle() {
  const open = useUi((s) => s.open);
  return (title: string) => {
    const page = searchCatalog(title, 5).find((p) => p.title.toLowerCase() === title.toLowerCase());
    if (page) open({ kind: 'page', pageId: page.id });
  };
}

/** Linked pages as chips that open the page. */
function LinkChips({ value, label }: { value: unknown; label: string }) {
  const { t } = useTranslation();
  const openTitle = useOpenByTitle();
  const titles = asList(value).map(linkTarget);
  if (!titles.length) return <span className="px-1.5 text-sm text-fg-muted">{t('db.empty')}</span>;
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1 px-1" aria-label={label}>
      {titles.map((title) => (
        <button
          key={title}
          type="button"
          onClick={() => openTitle(title)}
          className="flex max-w-full items-center gap-1 truncate rounded bg-bg-active px-1.5 py-px text-xs hover:underline"
        >
          <FileText className="size-3 shrink-0" />
          <span className="truncate">{title}</span>
        </button>
      ))}
    </span>
  );
}

/** Pick rows of the related database. */
function RelationValue({
  def,
  value,
  onChange,
  related,
}: {
  def: PropertyDef;
  value: unknown;
  onChange: (v: unknown) => void;
  related?: RelatedDatabases;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const target = def.relation ? related?.get(def.relation.databaseId) : undefined;
  const selected = asList(value).map(linkTarget);
  const isSelected = (title: string) =>
    selected.some((x) => x.toLowerCase() === title.toLowerCase());
  const rows = (target?.rows ?? []).filter(
    (r) => r.title && r.title.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const toggle = (title: string) => {
    const next = isSelected(title)
      ? selected.filter((x) => x.toLowerCase() !== title.toLowerCase())
      : [...selected, title];
    onChange(next.map((x) => `[[${x}]]`));
  };
  return (
    <Picker
      label={def.name}
      trigger={
        selected.length ? (
          selected.map((title) => (
            <span
              key={title}
              className="flex items-center gap-1 truncate rounded bg-bg-active px-1.5 py-px text-xs"
            >
              <FileText className="size-3 shrink-0" />
              {title}
            </span>
          ))
        ) : (
          <span className="text-sm text-fg-muted">{t('db.choose')}</span>
        )
      }
    >
      {!target ? (
        <p className="px-2 py-1 text-xs text-fg-muted">{t('db.relationNotSet')}</p>
      ) : (
        <>
          <input
            autoFocus
            value={query}
            aria-label={t('db.searchRows', { name: target.title })}
            placeholder={t('db.searchRows', { name: target.title })}
            onChange={(e) => setQuery(e.target.value)}
            className="mb-1 rounded-md border border-border bg-bg px-2 py-1 text-sm outline-none focus:border-accent"
          />
          <div role="listbox" aria-label={def.name} aria-multiselectable>
            {rows.map((r) => (
              <button
                key={r.id}
                type="button"
                role="option"
                aria-selected={isSelected(r.title)}
                onClick={() => toggle(r.title)}
                className="flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-bg-hover"
              >
                <FileText className="size-3.5 shrink-0 text-fg-muted" />
                <span className="min-w-0 flex-1 truncate">{r.title}</span>
                {isSelected(r.title) && <Check className="size-3.5 text-fg-muted" />}
              </button>
            ))}
            {rows.length === 0 && (
              <p className="px-2 py-1 text-xs text-fg-muted">{t('db.noRows')}</p>
            )}
          </div>
        </>
      )}
    </Picker>
  );
}

/** A computed value (rollup, formula), read-only. */
export function ComputedText({ def, value }: { def: PropertyDef; value: unknown }) {
  const { t, i18n } = useTranslation();
  if (value && typeof value === 'object' && !Array.isArray(value) && 'error' in value) {
    const message = String((value as { error: unknown }).error);
    return (
      <span
        title={message}
        aria-label={t('db.formulaError', { message })}
        className="px-1.5 text-sm text-danger"
      >
        #FOUT
      </span>
    );
  }
  if (value === null || value === undefined || value === '')
    return <span className="px-1.5 text-sm text-fg-muted" />;
  if (Array.isArray(value))
    return <span className="truncate px-1.5 text-sm">{value.join(', ')}</span>;
  const fn = def.rollup?.fn;
  if (typeof value === 'number') {
    const n = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(value);
    return (
      <span className="px-1.5 text-sm tabular-nums">
        {fn === 'percent_checked' || fn === 'percent_matching' ? `${n}%` : n}
      </span>
    );
  }
  if (typeof value === 'boolean')
    return <span className="px-1.5 text-sm">{value ? '✓' : '—'}</span>;
  const dt = /^\d{4}-\d{2}-\d{2}/.test(String(value)) ? splitDateTime(String(value)) : null;
  if (dt)
    return (
      <span className="px-1.5 text-sm">
        {formatDue(dt.date, i18n.language, t, undefined, dt.time)}
      </span>
    );
  return <span className="truncate px-1.5 text-sm">{String(value)}</span>;
}

function ActionButton({ def, rowId }: { def: PropertyDef; rowId?: string }) {
  const store = useStore();
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      disabled={!rowId || busy}
      onClick={async () => {
        if (!rowId) return;
        setBusy(true);
        try {
          if (def.button?.action === 'recur') await recurTaskPage(store, rowId);
        } finally {
          setBusy(false);
        }
      }}
      className="mx-1 flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs hover:bg-bg-hover disabled:opacity-50"
    >
      <RotateCw className="size-3" />
      {def.name}
    </button>
  );
}

export type RelatedDatabases = LoadedDatabase['related'];

/**
 * The same values with no editor at all -- a read-only workspace membership.
 * Links still navigate (that's reading, not writing); everything else is a
 * plain, non-interactive display.
 */
function ReadOnlyValue({ def, value }: { def: PropertyDef; value: unknown }) {
  switch (def.type) {
    case 'button':
      return null;
    case 'backlink':
    case 'relation':
    case 'page':
      return <LinkChips value={value} label={def.name} />;
    case 'select':
    case 'status':
    case 'multi_select':
    case 'person': {
      const values = asList(value).filter((v): v is string => typeof v === 'string' && v !== '');
      if (values.length === 0) return <span className="px-1.5 text-sm text-fg-muted" />;
      return (
        <span className="flex flex-wrap gap-1 px-1 py-0.5">
          {values.map((v) => (
            <OptionChip key={v} def={def} value={v} />
          ))}
        </span>
      );
    }
    case 'url':
    case 'email':
    case 'phone': {
      const text = value == null ? '' : String(value);
      if (!text) return <span className="px-1.5 text-sm text-fg-muted" />;
      const href =
        def.type === 'url'
          ? /^https?:\/\//.test(text)
            ? text
            : `https://${text}`
          : `${def.type === 'email' ? 'mailto' : 'tel'}:${text}`;
      return (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="truncate px-1.5 text-sm text-accent-ink hover:underline"
        >
          {text}
        </a>
      );
    }
    default:
      return <ComputedText def={def} value={value} />;
  }
}

/** The editor (or read-only view) for one value of a database property. */
export function PropertyValue({
  def,
  value,
  onChange,
  onDefChange,
  rowId,
  related,
  readOnly,
}: {
  def: PropertyDef;
  value: unknown;
  onChange: (v: unknown) => void;
  onDefChange: (def: PropertyDef) => void;
  /** The row (for actions like the recur button). */
  rowId?: string;
  /** Linked databases (for relation pickers). */
  related?: RelatedDatabases;
  /** A read-only workspace membership: no editor, links still navigate. */
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const emptyText = t('db.empty');
  const label = def.name;
  const text = value === undefined || value === null ? '' : String(value);
  if (readOnly) return <ReadOnlyValue def={def} value={value} />;
  switch (def.type) {
    case 'relation':
      return <RelationValue def={def} value={value} onChange={onChange} related={related} />;
    case 'backlink':
      return <LinkChips value={value} label={label} />;
    case 'rollup':
    case 'formula':
      return <ComputedText def={def} value={value} />;
    case 'button':
      return <ActionButton def={def} rowId={rowId} />;
    case 'select':
    case 'status':
    case 'multi_select':
      return <SelectValue def={def} value={value} onChange={onChange} onDefChange={onDefChange} />;
    case 'checkbox':
      return (
        <input
          type="checkbox"
          aria-label={label}
          checked={value === true || value === 'true'}
          onChange={(e) => onChange(e.target.checked)}
          className="m-1 size-4 accent-[var(--accent)]"
        />
      );
    case 'date':
      return <DateTimeEditor label={label} value={text} onChange={onChange} />;
    case 'reminder':
      return <ReminderEditor label={label} value={text} onChange={onChange} />;
    case 'number':
      return (
        <TextValue
          label={label}
          type="number"
          value={text}
          onChange={(v) => onChange(v === '' ? null : Number(v))}
        />
      );
    case 'person':
      return <SelectValue def={def} value={value} onChange={onChange} onDefChange={onDefChange} />;
    case 'page':
      return <PageValue label={label} value={value} onChange={onChange} />;
    case 'url':
    case 'email':
    case 'phone': {
      const href =
        def.type === 'url'
          ? /^https?:\/\//.test(text)
            ? text
            : `https://${text}`
          : `${def.type === 'email' ? 'mailto' : 'tel'}:${text}`;
      return (
        <span className="flex min-w-0 flex-1 items-center gap-1">
          <TextValue
            label={label}
            type={def.type === 'phone' ? 'tel' : def.type}
            value={text}
            onChange={(v) => onChange(v.trim() || null)}
          />
          {text && (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              aria-label={label}
              className="rounded p-0.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
            >
              <ExternalLink className="size-3.5" />
            </a>
          )}
        </span>
      );
    }
    default:
      return (
        <TextValue
          label={label}
          value={text}
          onChange={(v) => onChange(v)}
          placeholder={emptyText}
        />
      );
  }
}
